import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Trophy, Star, Calendar, Clock, Loader2, SlidersHorizontal, X, ListChecks, Heart, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { EmptyState } from '../../components/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { useLeagues, useFavouriteLeagues, useFavouriteTeams, useFixtures } from '../../hooks/useSports'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Kickoff time in the user's locale + 12h clock. The detail page
// gets a longer date+time format; this is the compact list view.
function fmtKickoffTime(iso, locale) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    })
  } catch {
    return iso
  }
}

function fmtKickoffDay(iso, locale) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    })
  } catch {
    return iso
  }
}

// One fixture card. The whole card links to /phoenix/sports/:id; the
// per-team star is a stop-propagation island. Status drives the
// centre column: scores when live/finished, kickoff time otherwise.
function FixtureCard({ fixture, locale, isArabic, favTeamIds, onToggleFavTeam }) {
  const isLive = fixture.status === 'live'
  const isFinished = fixture.status === 'finished'
  const hasScore = isLive || isFinished
  const league = fixture.league
  const leagueName = isArabic && league?.name_ar ? league.name_ar : league?.name
  return (
    <RouterLink to={`/sports/${fixture.api_id}`} className="block">
      <Card className="overflow-hidden hover:border-app-primary-500/40 transition-colors">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start gap-2 mb-2 text-[10px] text-muted-foreground" dir="auto">
            {league?.logo_url && (
              <img
                src={league.logo_url}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
                className="w-3.5 h-3.5 rounded-sm object-contain flex-shrink-0"
              />
            )}
            <span className="font-medium uppercase tracking-wide flex-1 min-w-0 truncate">
              {leagueName}
            </span>
            {fixture.round && (
              <span className="text-[10px] truncate">{fixture.round}</span>
            )}
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            {/* Home team */}
            <TeamColumn
              teamId={fixture.home_team_id}
              name={fixture.home_team}
              crestUrl={fixture.home_logo_url}
              isFavourite={favTeamIds.has(fixture.home_team_id)}
              onToggleFavourite={onToggleFavTeam}
            />

            {/* Centre — score or kickoff time */}
            <div className="flex flex-col items-center gap-1 min-w-[60px]">
              {hasScore ? (
                <p className={cn(
                  'text-xl sm:text-2xl font-bold font-mono tabular-nums',
                  isLive ? 'text-red-500' : 'text-foreground',
                )}>
                  {fixture.home_score ?? 0}
                  <span className="text-muted-foreground mx-1">–</span>
                  {fixture.away_score ?? 0}
                </p>
              ) : (
                <p className="text-sm font-semibold text-foreground font-mono tabular-nums" dir="ltr">
                  {fmtKickoffTime(fixture.kickoff_at, locale)}
                </p>
              )}
              {isLive && (
                <span className="text-[10px] font-semibold text-red-500 uppercase tracking-wide animate-pulse">
                  {fixture.elapsed_minutes != null ? `${fixture.elapsed_minutes}'` : 'LIVE'}
                </span>
              )}
              {isFinished && (
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  FT
                </span>
              )}
              {fixture.status === 'postponed' && (
                <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide" dir="auto">
                  {locale === 'ar' ? 'مؤجلة' : 'PST'}
                </span>
              )}
            </div>

            {/* Away team */}
            <TeamColumn
              teamId={fixture.away_team_id}
              name={fixture.away_team}
              crestUrl={fixture.away_logo_url}
              isFavourite={favTeamIds.has(fixture.away_team_id)}
              onToggleFavourite={onToggleFavTeam}
            />
          </div>

          {fixture.venue && (
            <p className="text-[10px] text-muted-foreground text-center mt-2 truncate" dir="auto">
              {fixture.venue}
            </p>
          )}
        </CardContent>
      </Card>
    </RouterLink>
  )
}

// Per-team column inside a fixture card. The star toggle is a tiny
// button that doesn't propagate up to the card link.
function TeamColumn({ teamId, name, crestUrl, isFavourite, onToggleFavourite }) {
  const handleToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!teamId || !onToggleFavourite) return
    onToggleFavourite({ id: teamId, name, shortName: name, crestUrl: crestUrl || null })
  }
  return (
    <div className="flex flex-col items-center gap-1.5">
      {crestUrl && (
        <img
          src={crestUrl}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none' }}
          className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
        />
      )}
      <p className="text-xs font-medium text-foreground text-center leading-tight" dir="auto">
        {name}
      </p>
      {teamId != null && (
        <button
          type="button"
          onClick={handleToggle}
          aria-pressed={isFavourite}
          aria-label={isFavourite ? 'unfavourite team' : 'favourite team'}
          className={cn(
            'p-0.5 rounded transition-colors',
            isFavourite ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500',
          )}
        >
          <Star className={cn('w-3 h-3', isFavourite && 'fill-current')} />
        </button>
      )}
    </div>
  )
}

function GroupHeader({ icon: Icon, label, count, accent }) {
  return (
    <div className="flex items-center gap-2 px-1 mb-2">
      <Icon className={cn('w-4 h-4', accent || 'text-app-primary-500')} />
      <p className="text-xs font-semibold uppercase tracking-wide text-foreground" dir="auto">
        {label}
      </p>
      <span className="text-[10px] text-muted-foreground">({count})</span>
    </div>
  )
}

// Hero card for the favourite team's next upcoming match. Hidden
// when no favourite team matches are in the visible window.
function FavouriteTeamHero({ fixture, locale, isArabic, t }) {
  if (!fixture) return null
  const league = fixture.league
  const leagueName = isArabic && league?.name_ar ? league.name_ar : league?.name
  const kickoff = new Date(fixture.kickoff_at)
  const now = Date.now()
  const diff = kickoff.getTime() - now
  const hoursAway = Math.max(0, Math.floor(diff / (3600 * 1000)))
  const inDays = Math.floor(hoursAway / 24)
  const subline = inDays > 1
    ? t('sports.hero.inDays', { count: inDays })
    : hoursAway > 0
      ? t('sports.hero.inHours', { count: hoursAway })
      : t('sports.hero.soon')
  return (
    <RouterLink to={`/sports/${fixture.api_id}`} className="block">
      <Card className="overflow-hidden border-app-primary-500/40 bg-app-primary-500/5 hover:bg-app-primary-500/10 transition-colors">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-1.5 mb-2 text-[10px] text-app-primary-500 font-semibold uppercase tracking-wide" dir="auto">
            <Heart className="w-3.5 h-3.5 fill-current" />
            <span>{t('sports.hero.title')}</span>
            <span className="ms-auto text-muted-foreground normal-case font-normal">{subline}</span>
          </div>
          <div className="flex items-center justify-center gap-3">
            {fixture.home_logo_url && (
              <img src={fixture.home_logo_url} alt="" loading="lazy" className="w-8 h-8 object-contain" />
            )}
            <span className="text-sm font-semibold text-foreground" dir="auto">{fixture.home_team}</span>
            <span className="text-xs text-muted-foreground">vs</span>
            <span className="text-sm font-semibold text-foreground" dir="auto">{fixture.away_team}</span>
            {fixture.away_logo_url && (
              <img src={fixture.away_logo_url} alt="" loading="lazy" className="w-8 h-8 object-contain" />
            )}
          </div>
          <p className="text-[11px] text-center text-muted-foreground mt-2" dir="auto">
            {leagueName} · <span dir="ltr">{fmtKickoffDay(fixture.kickoff_at, locale)} {fmtKickoffTime(fixture.kickoff_at, locale)}</span>
          </p>
        </CardContent>
      </Card>
    </RouterLink>
  )
}

// Inline chip below the filter bar so the user can see + clear active
// filters at a glance, just like the news page.
function FilterPill({ label, onClear }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-app-primary-500/10 text-app-primary-500 border border-app-primary-500/30"
      dir="auto"
    >
      <span className="truncate max-w-[140px]">{label}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label="clear filter"
        className="ms-0.5 hover:bg-app-primary-500/20 rounded-full p-0.5"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  )
}

export function SportsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const goBack = () => navigate('/')
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const isArabic = locale === 'ar'

  const leaguesHook = useLeagues()
  const favLeaguesHook = useFavouriteLeagues()
  const favTeamsHook = useFavouriteTeams()

  // Filter state. The popover writes back here when the user picks a
  // chip. "showAll" forces unfiltered when the user has favourite
  // leagues — otherwise the default is "narrow to favourites".
  // teamFilter narrows further to matches involving any favourite team.
  const [selectedLeagueId, setSelectedLeagueId] = useState(null)
  const [showAllLeagues, setShowAllLeagues] = useState(false)
  const [onlyMyTeams, setOnlyMyTeams] = useState(false)

  const effectiveLeagueIds = useMemo(() => {
    if (selectedLeagueId) return [selectedLeagueId]
    if (showAllLeagues) return null
    if (favLeaguesHook.favouriteIds.size > 0) return Array.from(favLeaguesHook.favouriteIds)
    return null
  }, [selectedLeagueId, showAllLeagues, favLeaguesHook.favouriteIds])

  const effectiveTeamIds = useMemo(() => {
    if (!onlyMyTeams) return null
    return [...favTeamsHook.teamIds]
  }, [onlyMyTeams, favTeamsHook.teamIds])

  const fixturesHook = useFixtures({ leagueIds: effectiveLeagueIds, teamIds: effectiveTeamIds })

  const handleRefresh = useCallback(
    () => performRefresh([fixturesHook.refetch, leaguesHook.refetch, favLeaguesHook.refetch, favTeamsHook.refetch]),
    [fixturesHook.refetch, leaguesHook.refetch, favLeaguesHook.refetch, favTeamsHook.refetch],
  )

  // Hero: pick the soonest upcoming fixture where home or away is a
  // favourite team. Only meaningful when the user has any favs.
  const heroFixture = useMemo(() => {
    if (favTeamsHook.teamIds.size === 0) return null
    return fixturesHook.fixtures.find((f) => {
      if (f.status !== 'scheduled') return false
      return favTeamsHook.teamIds.has(f.home_team_id) || favTeamsHook.teamIds.has(f.away_team_id)
    }) || null
  }, [fixturesHook.fixtures, favTeamsHook.teamIds])

  // League list for the popover. Favourites surface first.
  const orderedLeagues = useMemo(() => {
    const favs = []
    const others = []
    for (const l of leaguesHook.leagues) {
      if (favLeaguesHook.favouriteIds.has(l.id)) favs.push(l)
      else others.push(l)
    }
    return [...favs, ...others]
  }, [leaguesHook.leagues, favLeaguesHook.favouriteIds])

  const tableMissing = fixturesHook.tableMissing || leaguesHook.tableMissing
  const { live, today, tomorrow, later } = fixturesHook.buckets
  const hasAnyFixture = live.length + today.length + tomorrow.length + later.length > 0

  // Filter popover state — mirrors the news-page anchor pattern so
  // the dropdown's coords are pinned once on open.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filterTriggerRef = useRef(null)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const openFilters = useCallback(() => {
    if (filterTriggerRef.current) {
      setFilterAnchor(filterTriggerRef.current.getBoundingClientRect())
    }
    setFiltersOpen(true)
  }, [])
  useEffect(() => {
    if (!filtersOpen) return undefined
    const close = () => setFiltersOpen(false)
    const onKey = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
    }
  }, [filtersOpen])

  // What chip to highlight in the popover. When the user has
  // favourite leagues and hasn't picked one explicitly, surface
  // "Favourites" as active.
  const leagueChipActive = (l) => {
    if (selectedLeagueId) return selectedLeagueId === l.id
    return false
  }
  const favouritesChipActive = !showAllLeagues && !selectedLeagueId && favLeaguesHook.favouriteIds.size > 0
  const allChipActive = showAllLeagues && !selectedLeagueId

  const activeLeagueLabel = useMemo(() => {
    if (!selectedLeagueId) return null
    const l = leaguesHook.leagues.find((x) => x.id === selectedLeagueId)
    if (!l) return null
    return isArabic && l.name_ar ? l.name_ar : l.name
  }, [selectedLeagueId, leaguesHook.leagues, isArabic])

  const activeFilterCount = (selectedLeagueId ? 1 : 0) + (onlyMyTeams ? 1 : 0) + (showAllLeagues ? 1 : 0)

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5" dir="auto">
              <Trophy className="w-4 h-4 text-app-primary-500" />
              {t('sports.title')}
            </h1>
          </div>
          {/* Filter trigger — replaces the horizontal chip scroll. */}
          <button
            ref={filterTriggerRef}
            type="button"
            onClick={openFilters}
            className="p-2 -me-2 rounded-md hover:bg-accent text-foreground relative"
            aria-label={t('sports.filtersOpen')}
            aria-expanded={filtersOpen}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span className="absolute top-0.5 end-0.5 w-3.5 h-3.5 bg-app-primary-500 rounded-full text-[9px] text-white font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Active filter pill row — only shown when something explicit. */}
        {(activeLeagueLabel || onlyMyTeams || showAllLeagues) && (
          <div className="flex gap-1.5 flex-wrap">
            {showAllLeagues && (
              <FilterPill label={t('sports.filterAll')} onClear={() => setShowAllLeagues(false)} />
            )}
            {activeLeagueLabel && (
              <FilterPill label={activeLeagueLabel} onClear={() => setSelectedLeagueId(null)} />
            )}
            {onlyMyTeams && (
              <FilterPill label={t('sports.filterMyTeams')} onClear={() => setOnlyMyTeams(false)} />
            )}
          </div>
        )}

        {/* Favourite-team hero. Hidden when no favs / no upcoming. */}
        {heroFixture && !onlyMyTeams && (
          <FavouriteTeamHero fixture={heroFixture} locale={locale} isArabic={isArabic} t={t} />
        )}

        {tableMissing && (
          <Card>
            <CardContent className="p-3 text-xs text-amber-700 dark:text-amber-400" dir="auto">
              {t('sports.tableMissing')}
            </CardContent>
          </Card>
        )}

        {fixturesHook.loading ? (
          <div className="space-y-2">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : fixturesHook.error ? (
          <Card>
            <CardContent className="p-3 text-xs text-red-600" dir="auto">
              {fixturesHook.error}
            </CardContent>
          </Card>
        ) : !hasAnyFixture && !tableMissing ? (
          <EmptyState
            icon={Calendar}
            title={t('sports.empty.title')}
            description={onlyMyTeams ? t('sports.empty.descriptionTeams') : t('sports.empty.description')}
          />
        ) : (
          <div className="space-y-4">
            {live.length > 0 && (
              <section>
                <GroupHeader
                  icon={Loader2}
                  accent="text-red-500 animate-spin"
                  label={t('sports.groups.live')}
                  count={live.length}
                />
                <div className="space-y-2">
                  {live.map((f) => (
                    <FixtureCard
                      key={f.id}
                      fixture={f}
                      locale={locale}
                      isArabic={isArabic}
                      favTeamIds={favTeamsHook.teamIds}
                      onToggleFavTeam={favTeamsHook.toggle}
                    />
                  ))}
                </div>
              </section>
            )}
            {today.length > 0 && (
              <section>
                <GroupHeader icon={Clock} label={t('sports.groups.today')} count={today.length} />
                <div className="space-y-2">
                  {today.map((f) => (
                    <FixtureCard
                      key={f.id}
                      fixture={f}
                      locale={locale}
                      isArabic={isArabic}
                      favTeamIds={favTeamsHook.teamIds}
                      onToggleFavTeam={favTeamsHook.toggle}
                    />
                  ))}
                </div>
              </section>
            )}
            {tomorrow.length > 0 && (
              <section>
                <GroupHeader icon={Calendar} label={t('sports.groups.tomorrow')} count={tomorrow.length} />
                <div className="space-y-2">
                  {tomorrow.map((f) => (
                    <FixtureCard
                      key={f.id}
                      fixture={f}
                      locale={locale}
                      isArabic={isArabic}
                      favTeamIds={favTeamsHook.teamIds}
                      onToggleFavTeam={favTeamsHook.toggle}
                    />
                  ))}
                </div>
              </section>
            )}
            {later.length > 0 && (
              <section>
                <GroupHeader icon={Calendar} label={t('sports.groups.later')} count={later.length} />
                <div className="space-y-2">
                  {later.map((f) => {
                    const dayLabel = fmtKickoffDay(f.kickoff_at, locale)
                    return (
                      <div key={f.id} className="space-y-1">
                        <p className="text-[10px] text-muted-foreground px-1" dir="auto">
                          {dayLabel}
                        </p>
                        <FixtureCard
                          fixture={f}
                          locale={locale}
                          isArabic={isArabic}
                          favTeamIds={favTeamsHook.teamIds}
                          onToggleFavTeam={favTeamsHook.toggle}
                        />
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Filter popover — portal so it overlays without parent
            transforms creating a stacking context. */}
        {filtersOpen && filterAnchor && createPortal(
          <>
            <div
              className="fixed inset-0 z-[60]"
              onClick={() => setFiltersOpen(false)}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-modal="true"
              className="fixed z-[61] w-[min(90vw,360px)] max-h-[70vh] overflow-y-auto bg-card border border-border rounded-lg shadow-lg p-3 space-y-3"
              style={{
                top: filterAnchor.bottom + 6,
                // RTL safe: position from end-side by computing
                // a left value the browser can paint without RTL math.
                left: Math.max(8, Math.min(window.innerWidth - 380, filterAnchor.right - 360)),
              }}
            >
              {/* My teams toggle */}
              {favTeamsHook.teamIds.size > 0 && (
                <div className="flex items-start gap-2 pb-2 border-b border-border/60">
                  <Heart className="w-4 h-4 text-app-primary-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground" dir="auto">{t('sports.filterMyTeams')}</p>
                    <p className="text-[11px] text-muted-foreground" dir="auto">
                      {t('sports.filterMyTeamsHint', { count: favTeamsHook.teamIds.size })}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={onlyMyTeams}
                    onClick={() => setOnlyMyTeams((v) => !v)}
                    className={cn(
                      'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors mt-0.5',
                      onlyMyTeams ? 'bg-app-primary-500' : 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                        onlyMyTeams ? 'translate-x-4 rtl:-translate-x-4' : 'translate-x-0.5',
                      )}
                    />
                  </button>
                </div>
              )}

              <div className="space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1" dir="auto">
                  {t('sports.leaguesLabel')}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setShowAllLeagues(true); setSelectedLeagueId(null) }}
                    className={cn(
                      'flex items-center gap-1 px-2 py-1.5 rounded-md border text-xs font-medium',
                      allChipActive
                        ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                        : 'border-input text-muted-foreground hover:bg-accent',
                    )}
                  >
                    <ListChecks className="w-3 h-3" />
                    <span className="truncate">{t('sports.filterAll')}</span>
                  </button>
                  {favLeaguesHook.favouriteIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => { setShowAllLeagues(false); setSelectedLeagueId(null) }}
                      className={cn(
                        'flex items-center gap-1 px-2 py-1.5 rounded-md border text-xs font-medium',
                        favouritesChipActive
                          ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                          : 'border-input text-muted-foreground hover:bg-accent',
                      )}
                    >
                      <Star className="w-3 h-3 fill-current" />
                      <span className="truncate">{t('sports.filterFavourites')}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-1.5">
                {orderedLeagues.map((l) => {
                  const active = leagueChipActive(l)
                  const isFav = favLeaguesHook.favouriteIds.has(l.id)
                  const label = isArabic && l.name_ar ? l.name_ar : l.name
                  return (
                    <div
                      key={l.id}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-md border',
                        active
                          ? 'border-app-primary-500 bg-app-primary-500/10'
                          : 'border-input',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLeagueId(active ? null : l.id)
                          setShowAllLeagues(false)
                        }}
                        className="flex-1 flex items-center gap-2 text-start"
                      >
                        {l.logo_url && (
                          <img
                            src={l.logo_url}
                            alt=""
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                            className="w-4 h-4 object-contain flex-shrink-0"
                          />
                        )}
                        <span
                          className={cn(
                            'text-xs flex-1 min-w-0 truncate',
                            active ? 'text-app-primary-500 font-medium' : 'text-foreground',
                          )}
                          dir="auto"
                        >
                          {label}
                        </span>
                      </button>
                      {l.provider_code && (
                        <RouterLink
                          to={`/sports/standings/${l.id}`}
                          onClick={() => setFiltersOpen(false)}
                          className="text-[10px] text-muted-foreground hover:text-app-primary-500 px-1.5 py-0.5 rounded hover:bg-accent"
                          dir="auto"
                        >
                          {t('sports.viewStandings')}
                        </RouterLink>
                      )}
                      <button
                        type="button"
                        onClick={() => favLeaguesHook.toggle(l.id)}
                        aria-label={t(isFav ? 'sports.unfavourite' : 'sports.favourite')}
                        className={cn(
                          'p-0.5 rounded',
                          isFav ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500',
                        )}
                      >
                        <Star className={cn('w-3 h-3', isFav && 'fill-current')} />
                      </button>
                    </div>
                  )
                })}
              </div>

              {/* Favourited teams list (read-only here; managed from
                  match cards). Surfaces so the user can see what
                  they've starred and unstar quickly. */}
              {favTeamsHook.teams.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-border/60">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1" dir="auto">
                    {t('sports.myTeamsLabel')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {favTeamsHook.teams.map((tm) => (
                      <span
                        key={tm.team_id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-muted text-foreground"
                        dir="auto"
                      >
                        {tm.team_crest_url && (
                          <img src={tm.team_crest_url} alt="" loading="lazy" className="w-3 h-3 object-contain" />
                        )}
                        <span className="truncate max-w-[100px]">{tm.team_name}</span>
                        <button
                          type="button"
                          onClick={() => favTeamsHook.toggle({
                            id: tm.team_id,
                            name: tm.team_name,
                            shortName: tm.team_short_name,
                            crestUrl: tm.team_crest_url,
                          })}
                          aria-label={t('sports.unfavourite')}
                          className="hover:bg-accent rounded-full p-0.5"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
      </div>
    </PullToRefresh>
  )
}
